import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Calculator, ArrowRight, Check } from "lucide-react";
import api, { zar } from "@/lib/api";
import { PageHero } from "@/components/site/SiteBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const VEH = ["FTL", "LTL", "Container", "Refrigerated", "Flatbed", "Tanker"];
const PRI = ["Standard", "Express", "Critical"];

export default function Quote() {
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    origin_city: "Cape Town", dest_city: "Johannesburg", weight_kg: 5000, volume_cbm: 30,
    vehicle_type: "FTL", priority: "Standard", insurance: false, handling: false, cargo_value_zar: 250000,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/public/cities").then((r) => setCities(r.data.cities)); }, []);

  const calc = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/public/quote", {
        ...form, weight_kg: Number(form.weight_kg), volume_cbm: Number(form.volume_cbm), cargo_value_zar: Number(form.cargo_value_zar),
      });
      setResult(data);
      toast.success("Quotation generated");
    } catch { toast.error("Could not generate quote"); }
    finally { setLoading(false); }
  };

  const LABELS = {
    base_fee: "Base fee", distance_cost: "Distance", weight_cost: "Weight", volume_cost: "Volume",
    fuel_surcharge: "Fuel surcharge", tolls: "Tolls", priority_uplift: "Priority uplift",
    insurance: "Insurance", handling: "Handling", cross_border: "Cross-border fee",
  };

  return (
    <div>
      <PageHero eyebrow="Instant Quotation" title="Request a freight quote"
        subtitle="Get a transparent, itemised estimate in ZAR and USD across Southern African corridors." />
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-12 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 rounded-2xl border border-border bg-card p-6 lg:p-8 space-y-5">
          <h2 className="font-heading font-extrabold text-xl flex items-center gap-2"><Calculator size={20} className="text-primary" /> Shipment details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Origin</Label>
              <Select value={form.origin_city} onValueChange={(v) => set("origin_city", v)}>
                <SelectTrigger data-testid="quote-origin" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Destination</Label>
              <Select value={form.dest_city} onValueChange={(v) => set("dest_city", v)}>
                <SelectTrigger data-testid="quote-dest" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Weight (kg)</Label><Input data-testid="quote-weight" type="number" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Volume (CBM)</Label><Input data-testid="quote-volume" type="number" value={form.volume_cbm} onChange={(e) => set("volume_cbm", e.target.value)} className="mt-1.5" /></div>
            <div>
              <Label>Vehicle type</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => set("vehicle_type", v)}>
                <SelectTrigger data-testid="quote-vehicle" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{VEH.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="quote-priority" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{PRI.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Cargo value (ZAR)</Label><Input data-testid="quote-value" type="number" value={form.cargo_value_zar} onChange={(e) => set("cargo_value_zar", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-3 text-sm"><Switch checked={form.insurance} onCheckedChange={(v) => set("insurance", v)} data-testid="quote-insurance" /> Cargo insurance</label>
            <label className="flex items-center gap-3 text-sm"><Switch checked={form.handling} onCheckedChange={(v) => set("handling", v)} data-testid="quote-handling" /> Special handling</label>
          </div>
          <Button data-testid="quote-calc" onClick={calc} disabled={loading} className="w-full bg-primary hover:bg-red-700 h-11">
            {loading ? "Calculating…" : "Generate Quotation"} <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-border bg-[#0B0F17] text-white p-6 lg:p-8 sticky top-24" data-testid="quote-result">
            <p className="eyebrow text-primary">Your Estimate</p>
            {!result ? (
              <p className="mt-6 text-slate-400 text-sm">Fill in your shipment details and generate a quote to see an itemised breakdown.</p>
            ) : (
              <div className="mt-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-heading font-extrabold">{zar(result.total_zar)}</span>
                </div>
                <p className="text-slate-400 text-sm mt-1">≈ ${result.total_usd.toLocaleString()} USD · {result.distance_km} km {result.cross_border && "· Cross-border"}</p>
                <div className="mt-5 space-y-2 border-t border-white/10 pt-4">
                  {Object.entries(result.breakdown).filter(([, v]) => v > 0).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="text-slate-400">{LABELS[k] || k}</span>
                      <span className="font-mono">{zar(v)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-between border-t border-white/10 pt-4 font-heading font-bold">
                  <span>Total (excl. VAT)</span><span>{zar(result.total_zar)}</span>
                </div>
                <ul className="mt-5 space-y-1.5 text-xs text-slate-400">
                  {["Valid for 14 days", "Fully insured options", "Real-time tracking included"].map((x) => (
                    <li key={x} className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> {x}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
